import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner, Form, Modal, Badge } from 'react-bootstrap';
import axios from '../utils/api';
import { useCart } from '../contexts/CartContext';
import { useStoreSettings } from '../contexts/StoreSettingsContext';

const CartPage = () => {
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    updateItemQuantity,
    clearCart,
    checkout,
    checkoutLoading,
    checkoutError,
    checkoutErrorCode,
    checkoutSuccess,
    calculateCartTotals,
    applyPromo,
    removePromo,
  } = useCart();
  const { settings: storeSettings } = useStoreSettings();
  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [emailStatus, setEmailStatus] = useState({
    loading: true,
    email: '',
    email_verified: false,
  });
  const [phoneStatus, setPhoneStatus] = useState({
    loading: true,
    phone: '',
    phone_verified: false,
  });
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [phoneOtpValue, setPhoneOtpValue] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [phoneOtpSending, setPhoneOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [phoneOtpVerifying, setPhoneOtpVerifying] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState('');
  const [phoneVerificationInfo, setPhoneVerificationInfo] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [phoneVerificationError, setPhoneVerificationError] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');

  const cartMessages = useMemo(() => {
    if (Array.isArray(storeSettings?.cart_messages) && storeSettings.cart_messages.length > 0) {
      return storeSettings.cart_messages;
    }

    return [
      'Free shipping on orders over $50',
      '30-day return policy',
      'Secure checkout',
    ];
  }, [storeSettings]);

  const formatIndianAmount = (value) => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setAddressLoading(true);
        const response = await axios.get('/api/buyer-addresses');
        const loadedAddresses = response.data || [];
        setAddresses(loadedAddresses);
        const defaultAddress = loadedAddresses.find((address) => address.is_default) || loadedAddresses[0];
        setSelectedAddressId(defaultAddress ? String(defaultAddress.id) : '');
      } catch (error) {
        setAddresses([]);
        setSelectedAddressId('');
      } finally {
        setAddressLoading(false);
      }
    };

    fetchAddresses();
  }, []);

  const fetchEmailVerificationStatus = async () => {
    try {
      const response = await axios.get('/api/email-verification/status');
      const status = response.data || {};
      const normalized = {
        loading: false,
        email: status.email || '',
        email_verified: Boolean(status.email_verified),
      };
      setEmailStatus(normalized);
      return normalized;
    } catch {
      const fallback = {
        loading: false,
        email: '',
        email_verified: false,
      };
      setEmailStatus(fallback);
      return fallback;
    }
  };

  const fetchPhoneVerificationStatus = async () => {
    try {
      const response = await axios.get('/api/phone-verification/status');
      const status = response.data || {};
      const normalized = {
        loading: false,
        phone: `${status.country_isd_code || ''} ${status.phone_number || ''}`.trim(),
        phone_verified: Boolean(status.phone_verified),
      };
      setPhoneStatus(normalized);
      return normalized;
    } catch {
      const fallback = {
        loading: false,
        phone: '',
        phone_verified: false,
      };
      setPhoneStatus(fallback);
      return fallback;
    }
  };

  useEffect(() => {
    fetchEmailVerificationStatus();
    fetchPhoneVerificationStatus();
  }, []);

  useEffect(() => {
    if (checkoutErrorCode === 'phone_verification_required') {
      setShowPhoneVerificationModal(true);
      setPhoneVerificationError('');
      setPhoneVerificationInfo('Please verify your phone on WhatsApp to continue checkout.');
      sendPhoneOtp();
    }
  }, [checkoutErrorCode]);

  const sendOtp = async () => {
    try {
      setOtpSending(true);
      setVerificationError('');
      const response = await axios.post('/api/email-verification/send-otp');
      setVerificationInfo(response.data?.message || 'OTP sent to your email.');
    } catch (error) {
      setVerificationError('Error sendinng OTP. Please try after sometime.');
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtpAndResumeCheckout = async () => {
    if (!/^\d{4}$/.test(otpValue.trim())) {
      setVerificationError('Enter a valid 4-digit OTP.');
      return;
    }

    try {
      setOtpVerifying(true);
      setVerificationError('');
      await axios.post('/api/email-verification/verify-otp', { otp: otpValue.trim() });
      setVerificationInfo('Email verified successfully. Continuing checkout...');
      setOtpValue('');
      setShowEmailVerificationModal(false);
      await fetchEmailVerificationStatus();
      checkout(selectedAddressId ? Number(selectedAddressId) : null);
    } catch (error) {
      setVerificationError(error.response?.data?.detail || 'Invalid or expired OTP.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const sendPhoneOtp = async () => {
    try {
      setPhoneOtpSending(true);
      setPhoneVerificationError('');
      const response = await axios.post('/api/phone-verification/send-otp');
      setPhoneVerificationInfo(response.data?.message || 'OTP sent to WhatsApp.');
    } catch (error) {
      setPhoneVerificationError(error.response?.data?.detail || 'Error sending OTP. Please try after sometime.');
    } finally {
      setPhoneOtpSending(false);
    }
  };

  const verifyPhoneOtp = async () => {
    if (!/^\d{6}$/.test(phoneOtpValue.trim())) {
      setPhoneVerificationError('Enter a valid 6-digit OTP.');
      return;
    }

    try {
      setPhoneOtpVerifying(true);
      setPhoneVerificationError('');
      await axios.post('/api/phone-verification/verify-otp', { otp: phoneOtpValue.trim() });
      setPhoneVerificationInfo('Phone verified successfully.');
      setPhoneOtpValue('');
      setShowPhoneVerificationModal(false);
      await fetchPhoneVerificationStatus();
    } catch (error) {
      setPhoneVerificationError(error.response?.data?.detail || 'Invalid or expired OTP.');
    } finally {
      setPhoneOtpVerifying(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    const newQuantity = Math.max(1, Number(value));
    updateItemQuantity(itemId, newQuantity);
  };

  const handleCheckout = async () => {
    const status = await fetchPhoneVerificationStatus();
    if (!status.phone_verified) {
      setShowPhoneVerificationModal(true);
      setPhoneVerificationInfo('Please verify your phone on WhatsApp to continue checkout.');
      setPhoneVerificationError('');
      await sendPhoneOtp();
      return;
    }

    checkout(selectedAddressId ? Number(selectedAddressId) : null);
  };

  const appliedPromoCode = items.length > 0 ? items[0].applied_promo_code : null;

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    const result = await applyPromo(promoInput.trim().toUpperCase());
    setPromoLoading(false);
    if (result.success) {
      setPromoInput('');
    } else {
      setPromoError(result.error || 'Invalid promo code');
    }
  };

  const handleRemovePromo = async () => {
    setPromoError('');
    await removePromo();
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1>Shopping Cart</h1>
              <p className="text-muted">
                {items.length === 0 ? 'Your cart is empty' : `${items.length} item${items.length > 1 ? 's' : ''} in your cart`}
              </p>
            </div>
            <Button variant="primary" href="/buyer/orders">
              View Orders
            </Button>
          </div>
        </Col>
      </Row>

      {items.length === 0 ? (
        <Row>
          <Col md={6} className="mx-auto">
            <Card className="text-center">
              <Card.Body className="py-5">
                <h4>Your cart is empty</h4>
                <p className="text-muted">Add some products to get started!</p>
                <Button variant="primary" href="/">
                  Browse Products
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col>
              <Card>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Variant</th>
                          <th>Price</th>
                          <th>GST</th>
                          <th>Total Price Per Unit</th>
                          <th>Quantity</th>
                          <th>Subtotal</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const hasItemPromo = item.promo_final_per_unit != null;
                          const effectiveUnit = hasItemPromo ? item.promo_final_per_unit : (item.final_sp_unit_discount ?? item.price);
                          const subtotal = effectiveUnit * item.quantity;
                          return (
                            <tr key={item.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {item.thumbnail_url ? (
                                    <img
                                      src={item.thumbnail_url}
                                      alt={item.product_name}
                                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                    />
                                  ) : (
                                    <div style={{ width: 48, height: 48, background: '#e9ecef', borderRadius: 4, flexShrink: 0 }} />
                                  )}
                                  <span className="fw-bold">{item.product_name}</span>
                                </div>
                              </td>
                              <td>{item.variant_name}</td>
                              <td className="fw-bold">{item.currency} {formatIndianAmount(item.price)}</td>
                              <td>
                                {hasItemPromo ? (
                                  <>
                                    <span className="text-decoration-line-through text-muted me-1">
                                      {item.currency} {formatIndianAmount(item.gst_after_discount ?? 0)}
                                    </span>
                                    <span className="text-success fw-semibold">
                                      {item.currency} {formatIndianAmount(item.promo_gst_per_unit ?? 0)}
                                    </span>
                                  </>
                                ) : (
                                  <>{item.currency} {formatIndianAmount(item.gst_after_discount ?? 0)}</>
                                )}
                              </td>
                              <td className="fw-semibold">
                                {hasItemPromo ? (
                                  <div>
                                    <div className="text-decoration-line-through text-muted" style={{ fontSize: '0.85em' }}>
                                      {item.currency} {formatIndianAmount(item.final_sp_unit_discount ?? item.price)}
                                    </div>
                                    <div className="text-success fw-bold">
                                      {item.currency} {formatIndianAmount(item.promo_final_per_unit)}
                                    </div>
                                  </div>
                                ) : (
                                  <>{item.currency} {formatIndianAmount(item.final_sp_unit_discount ?? item.price)}</>
                                )}
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  min="1"
                                  max={item.max_stock || 999}
                                  value={item.quantity}
                                  onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                  style={{ width: '80px' }}
                                  size="sm"
                                />
                              </td>
                              <td className="fw-bold">
                                {item.currency} {formatIndianAmount(subtotal)}
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeItem(item.id)}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={8}>
              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h5 className="mb-1">Shipping Address</h5>
                      <p className="text-muted mb-0">Choose the address for this purchase.</p>
                    </div>
                    <Button variant="outline-primary" onClick={() => navigate('/buyer/addresses')}>
                      Manage Addresses
                    </Button>
                  </div>

                  {addressLoading ? (
                    <Spinner animation="border" size="sm" role="status" />
                  ) : addresses.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      No shipping address found. Add an address before checkout.
                    </Alert>
                  ) : (
                    <Form>
                      {addresses.map((address) => (
                        <Form.Check
                          key={address.id}
                          type="radio"
                          id={`shipping-address-${address.id}`}
                          name="shippingAddress"
                          value={address.id}
                          checked={selectedAddressId === String(address.id)}
                          onChange={(event) => setSelectedAddressId(event.target.value)}
                          label={
                            <span>
                              <strong>{address.name || address.addr_line1}</strong>
                              {address.is_default ? ' (Default)' : ''}
                              {address.is_billing ? ' (Billing)' : ''}
                              <br />
                              {address.contact_isd_code} {address.contact_number}
                              <br />
                              {address.addr_line1}
                              <br />
                              {address.addr_line2 ? `${address.addr_line2}, ` : ''}
                              {address.city}, {address.state}, India - {address.pincode}
                            </span>
                          }
                          className="mb-3"
                        />
                      ))}
                    </Form>
                  )}
                </Card.Body>
              </Card>

              <Button variant="outline-secondary" onClick={clearCart}>
                Clear Cart
              </Button>
            </Col>
            <Col md={4}>
              <Card>
                <Card.Body>
                  {(() => {
                    const currency = items[0]?.currency || 'INR';
                    const { priceTotal, totalGst, grandTotal, originalGrandTotal, promoSaving, hasPromo } = calculateCartTotals(items);
                    const selectedAddress = addresses.find((a) => String(a.id) === selectedAddressId);
                    const isIntraState = selectedAddress && storeSettings.store_state &&
                      selectedAddress.state.trim().toLowerCase() === storeSettings.store_state.trim().toLowerCase();
                    return (
                      <>
                        <table className="w-100 mb-3" style={{ fontSize: '0.95rem' }}>
                          <tbody>
                            <tr>
                              <td className="py-1">Price Total</td>
                              <td className="py-1 text-end">{currency} {formatIndianAmount(priceTotal)}</td>
                            </tr>
                            {isIntraState ? (
                              <>
                                <tr>
                                  <td className="py-1">CGST (50%)</td>
                                  <td className="py-1 text-end">{currency} {formatIndianAmount(totalGst / 2)}</td>
                                </tr>
                                <tr>
                                  <td className="py-1">SGST (50%)</td>
                                  <td className="py-1 text-end">{currency} {formatIndianAmount(totalGst / 2)}</td>
                                </tr>
                              </>
                            ) : (
                              <tr>
                                <td className="py-1">IGST (100%)</td>
                                <td className="py-1 text-end">{currency} {formatIndianAmount(totalGst)}</td>
                              </tr>
                            )}
                            {hasPromo && promoSaving > 0 && (
                              <>
                                <tr>
                                  <td className="py-1 text-muted text-decoration-line-through">Subtotal (before promo)</td>
                                  <td className="py-1 text-end text-muted text-decoration-line-through">{currency} {formatIndianAmount(originalGrandTotal)}</td>
                                </tr>
                                <tr>
                                  <td className="py-1 text-success fw-semibold">
                                    Promo Savings ({appliedPromoCode})
                                  </td>
                                  <td className="py-1 text-end text-success fw-semibold">- {currency} {formatIndianAmount(promoSaving)}</td>
                                </tr>
                              </>
                            )}
                            <tr className="border-top fw-bold">
                              <td className="pt-2">Grand Total</td>
                              <td className="pt-2 text-end text-primary" style={{ fontSize: '1.1rem' }}>{currency} {formatIndianAmount(grandTotal)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </>
                    );
                  })()}

                  {/* Promo code input */}
                  <div className="mb-3">
                    {appliedPromoCode ? (
                      <div className="d-flex align-items-center gap-2 p-2 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <span className="text-success fw-semibold flex-grow-1">
                          🎉 Promo applied: <strong>{appliedPromoCode}</strong>
                          {items[0]?.promo_discount_percent != null && (
                            <span className="ms-1 text-muted" style={{ fontSize: '0.85em' }}>
                              ({items[0].promo_discount_percent}% off)
                            </span>
                          )}
                        </span>
                        <Button variant="link" size="sm" className="text-danger p-0" onClick={handleRemovePromo}>
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Form.Label className="small text-muted mb-1">Have a promo code?</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder="Enter code"
                            value={promoInput}
                            onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                            maxLength={20}
                          />
                          <Button variant="outline-primary" size="sm" onClick={handleApplyPromo} disabled={promoLoading || !promoInput.trim()}>
                            {promoLoading ? '...' : 'Apply'}
                          </Button>
                        </div>
                        {promoError && <div className="text-danger small mt-1">{promoError}</div>}
                      </>
                    )}
                  </div>

                  <div className="d-grid">
                    <div className="mb-2 d-flex align-items-center justify-content-between">
                      <small className="text-muted">Email verification</small>
                      <div className="d-flex align-items-center gap-2">
                        {emailStatus.loading ? (
                          <Spinner animation="border" size="sm" role="status" />
                        ) : emailStatus.email_verified ? (
                          <Badge bg="success">Verified</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Pending</Badge>
                        )}
                        {!emailStatus.loading && !emailStatus.email_verified && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={async () => {
                              setShowEmailVerificationModal(true);
                              setVerificationInfo('');
                              setVerificationError('');
                              setOtpValue('');
                              await sendOtp();
                            }}
                          >
                            Verify
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mb-2 d-flex align-items-center justify-content-between">
                      <small className="text-muted">Phone verification for WhatsApp</small>
                      <div className="d-flex align-items-center gap-2">
                        {phoneStatus.loading ? (
                          <Spinner animation="border" size="sm" role="status" />
                        ) : phoneStatus.phone_verified ? (
                          <Badge bg="success">Verified</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Pending</Badge>
                        )}
                        {!phoneStatus.loading && !phoneStatus.phone_verified && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={async () => {
                              setShowPhoneVerificationModal(true);
                              setPhoneVerificationInfo('');
                              setPhoneVerificationError('');
                              setPhoneOtpValue('');
                              await sendPhoneOtp();
                            }}
                          >
                            Verify
                          </Button>
                        )}
                      </div>
                    </div>
                    <small className="text-muted mb-2 d-block">
                      Communication for the order will be sent on verified email or whatsapp.
                    </small>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={handleCheckout}
                      disabled={checkoutLoading || addressLoading || addresses.length === 0 || !selectedAddressId || storeSettings.razorpay_enabled === false}
                    >
                      {checkoutLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Processing Order...
                        </>
                      ) : addresses.length === 0 ? (
                        'Add Address'
                      ) : (
                        'Complete Purchase'
                      )}
                    </Button>
                    {storeSettings.razorpay_enabled === false && (
                      <small className="text-danger mt-2 d-block">Online payments are currently unavailable.</small>
                    )}
                  </div>

                  <div className="mt-3 text-muted small">
                    <ul className="mb-0 ps-3">
                      {cartMessages.map((message, index) => (
                        <li key={`${index}-${message}`}>{message}</li>
                      ))}
                    </ul>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {checkoutError && (
            <Row className="mt-3">
              <Col>
                <Alert variant="danger">
                  <div>{checkoutError}</div>
                  {checkoutErrorCode === 'unpaid_order_exists' && (
                    <div className="mt-3 d-flex gap-2 flex-wrap">
                      <Button variant="outline-light" size="sm" onClick={() => navigate('/buyer/orders')}>
                        Go to Orders
                      </Button>
                    </div>
                  )}
                </Alert>
              </Col>
            </Row>
          )}

          {checkoutSuccess && (
            <Row className="mt-3">
              <Col>
                <Alert variant="success">
                  {checkoutSuccess}
                </Alert>
              </Col>
            </Row>
          )}

          <Modal show={showEmailVerificationModal} onHide={() => setShowEmailVerificationModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Verify Your Email</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-2">
                Enter the 4-digit OTP sent to {emailStatus.email || 'your registered email'}.
              </p>

              {verificationInfo && <Alert variant="info" className="py-2">{verificationInfo}</Alert>}
              {verificationError && <Alert variant="danger" className="py-2">{verificationError}</Alert>}

              <Form.Group className="mb-3" controlId="emailOtpInput">
                <Form.Label>OTP</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={otpValue}
                  onChange={(event) => setOtpValue(event.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="Enter 4-digit OTP"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={sendOtp} disabled={otpSending || otpVerifying}>
                {otpSending ? 'Sending...' : 'Resend OTP'}
              </Button>
              <Button variant="primary" onClick={verifyOtpAndResumeCheckout} disabled={otpSending || otpVerifying}>
                {otpVerifying ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showPhoneVerificationModal} onHide={() => setShowPhoneVerificationModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Verify Your Phone for WhatsApp</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-2">
                Enter the 6-digit OTP sent to {phoneStatus.phone || 'your registered phone number'}.
              </p>

              {phoneVerificationInfo && <Alert variant="info" className="py-2">{phoneVerificationInfo}</Alert>}
              {phoneVerificationError && <Alert variant="danger" className="py-2">{phoneVerificationError}</Alert>}

              <Form.Group className="mb-3" controlId="phoneOtpInput">
                <Form.Label>OTP</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={phoneOtpValue}
                  onChange={(event) => setPhoneOtpValue(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={sendPhoneOtp} disabled={phoneOtpSending || phoneOtpVerifying}>
                {phoneOtpSending ? 'Sending...' : 'Resend OTP'}
              </Button>
              <Button variant="primary" onClick={verifyPhoneOtp} disabled={phoneOtpSending || phoneOtpVerifying}>
                {phoneOtpVerifying ? 'Verifying...' : 'Verify Phone'}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default CartPage;
